import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { DEFAULT_RANGE_IDS } from 'constants/prices';

import { ColumnsSectionContainer, SectionColumn, SectionContainer } from 'components/DetailsView/Section';
import { DetailFormArrayValue } from 'components/DetailsView/DetailFormArrayValue';
import { DetailFormCheckbox, DetailFormSelect, DetailFormValue, DetailValue } from 'components/DetailsView';
import { Loader } from 'components/Loader';
import { TotemImage } from 'components/TotemImage';
import { CopyValue } from 'components/CopyValue';

import { getExtendedFormattedDatetime } from 'helpers/dateTimes';
import { getOdooPartnerUrl } from 'helpers/odoo';
import { getStripeCustomerUrl } from 'helpers/stripe';
import {
    GetOrganizationQuery,
    useGetProductArrangementsQuery,
    useGetProductPriceRangesQuery,
} from 'data/__generated__';

import { ODOO_COMPANY_LABELS } from 'pages/Suppliers/constants/labels';

import { StatesSection } from './StatesSection';
import { AdditionalInfoSection } from './AdditionalInfoSection';

function getAvaLink(avaClientId?: number | null): string {
    return 'https://neocorner.app.ava-vending.fr/index.html#/customers/' + avaClientId + '/info';
}

export const GeneralInfoSection = ({ organization }: { organization: GetOrganizationQuery['organization'] }) => {
    const {
        _id,
        attachUsersManually,
        B2BPriceRange,
        createdAt,
        hasSubscription,
        imageUrl,
        name,
        stripeId,
        updatedAt,
        avaClientId,
        odooOrganizationId,
        odooCompany,
        restrictedProductArrangementId,
        customerType,
    } = organization;

    const {
        loading: priceRangesLoading,
        data: priceRangesData,
        error: priceRangesError,
    } = useGetProductPriceRangesQuery();

    const {
        loading: productArrangementsLoading,
        data: productArrangementsData,
        error: productArrangementsError,
    } = useGetProductArrangementsQuery();

    const { register } = useFormContext();

    if (priceRangesLoading || productArrangementsLoading) {
        return (
            <SectionContainer title="Informations générales" isInitiallyOpen>
                <Loader />
            </SectionContainer>
        );
    }

    if (priceRangesError || !priceRangesData || productArrangementsError || !productArrangementsData) {
        throw new Error('Une erreur est survenue lors de la récupération des gammes');
    }

    const { productPriceRanges } = priceRangesData;
    const { productArrangementsQuery: productArrangements } = productArrangementsData;
    const rangesWithoutDefault = productPriceRanges.filter(({ _id }) => !DEFAULT_RANGE_IDS.includes(_id));
    const rangeOptions = rangesWithoutDefault.map(({ _id, name }) => ({ label: name, value: _id }));
    const productArrangementOptions = productArrangements.map(({ _id, name }) => ({
        label: name,
        value: _id,
    }));

    const customerTypeOptions = [
        { label: 'TELECOMMUNICATION', value: 'TELECOMMUNICATION' },
        { label: 'DIVERS', value: 'DIVERS' },
        { label: 'AGENCE', value: 'AGENCE' },
        { label: 'COUVENT', value: 'COUVENT' },
        { label: 'AMBASSADE', value: 'AMBASSADE' },
        { label: 'ASSOCIATION', value: 'ASSOCIATION' },
        { label: 'ENSEIGNEMENT', value: 'ENSEIGNEMENT' },
        { label: 'AUTOMOBILES', value: 'AUTOMOBILES' },
        { label: 'PUBLICITE', value: 'PUBLICITE' },
        { label: 'DISTRIBUTEUR AUTO', value: 'DISTRIBUTEUR AUTO' },
        { label: 'HOPITAUX', value: 'HOPITAUX' },
        { label: 'HOTELS', value: 'HOTELS' },
        { label: 'IMPOTS', value: 'IMPOTS' },
        { label: 'FAST FOOD', value: 'FAST FOOD' },
        { label: 'CHOCOLATERIE', value: 'CHOCOLATERIE' },
        { label: 'BANQUE', value: 'BANQUE' },
        { label: 'SECURITE', value: 'SECURITE' },
        { label: 'POSTE', value: 'POSTE' },
        { label: 'ALIMENTATION', value: 'ALIMENTATION' },
        { label: 'ADMINISTRATION', value: 'ADMINISTRATION' },
        { label: 'CAT', value: 'CAT' },
        { label: "CABINET D'AVOCATS", value: "CABINET D'AVOCATS" },
        { label: 'RESTAURANT', value: 'RESTAURANT' },
        { label: 'CLINIQUES', value: 'CLINIQUES' },
        { label: 'MAIRIE', value: 'MAIRIE' },
        { label: 'SANTE', value: 'SANTE' },
        { label: 'MUSEE', value: 'MUSEE' },
        { label: 'IMMOBILIER', value: 'IMMOBILIER' },
        { label: 'CENTRE COMMERCIAL', value: 'CENTRE COMMERCIAL' },
        { label: 'INFORMATIQUE', value: 'INFORMATIQUE' },
        { label: 'SPORT', value: 'SPORT' },
        { label: 'ARMEE', value: 'ARMEE' },
        { label: 'MAGAZINE', value: 'MAGAZINE' },
        { label: "MAISON D'EDITION", value: "MAISON D'EDITION" },
        { label: 'ESSENCE', value: 'ESSENCE' },
        { label: 'LABORATOIRES', value: 'LABORATOIRES' },
        { label: 'LYCEE', value: 'LYCEE' },
    ];

    return (
        <SectionContainer title="Informations générales" isInitiallyOpen>
            <ColumnsSectionContainer numberOfColumns={2}>
                <SectionColumn>
                    <CopyValue value={_id} />
                    <Header>
                        <TotemImage src={imageUrl || ''} alt="Logo de l'organisation" size={150} />
                        <SideValues>
                            <DetailValue label="Nom" value={name} />
                            <DetailValue label="Date de création" value={getExtendedFormattedDatetime(createdAt)} />
                            <DetailValue label="Dernière mise à jour" value={getExtendedFormattedDatetime(updatedAt)} />
                        </SideValues>
                    </Header>
                    <DetailFormArrayValue name="domains" label="Domaines" />
                    <DetailFormCheckbox
                        name="attachUsersManually"
                        label="Valider manuellement les rattachements"
                        sublabel="Les personnes qui s'inscrivent avec un des domaines ci-dessus doivent être validées par un admin de l'entreprise pour être rattachées et bénéficier des subventions. Sinon le rattachement est automatique"
                        defaultChecked={attachUsersManually}
                    />
                    <DetailFormSelect
                        label="Gamme de prix"
                        placeholder="Sélectionner une gamme de prix"
                        defaultValue={B2BPriceRange}
                        name="B2BPriceRange"
                        options={rangeOptions}
                        isClearable
                    />
                    <DetailFormSelect
                        label="Restreindre les produits disponibles"
                        placeholder="Sélectionner une gamme de produits"
                        defaultValue={restrictedProductArrangementId}
                        name="restrictedProductArrangementId"
                        options={productArrangementOptions}
                        isClearable
                    />
                    <DetailFormSelect
                        label="Type de client"
                        placeholder="Sélectionner un type de client"
                        defaultValue={customerType}
                        name="customerType"
                        options={customerTypeOptions}
                    />
                    <DetailFormCheckbox
                        name="hasSubscription"
                        label="Le client ne paie pas les livraisons"
                        sublabel="Client store avec les livraisons incluses ou abonnement livraisons illimitées. Attention : mettre à jour l'abonnement sur Stripe également pour les clients freefood"
                        defaultChecked={!!hasSubscription}
                    />
                    <DetailValue
                        label="Société Odoo"
                        value={odooCompany ? ODOO_COMPANY_LABELS[odooCompany] : 'Non renseignée'}
                    />
                    <ExternalResource>
                        <ExternalResourceLink
                            href={getStripeCustomerUrl(stripeId)}
                            target="_blank"
                            rel="noopener noreferrer"
                            $disabled={!stripeId}
                        >
                            <TotemImage src="https://dashboard.stripe.com/favicon.ico" alt="Logo Stripe" size={30} />
                            <span>{stripeId || 'Pas de stripeId'}</span>
                        </ExternalResourceLink>
                    </ExternalResource>
                    <ExternalResource>
                        <ExternalResourceLink
                            href={odooOrganizationId ? getOdooPartnerUrl(odooOrganizationId) : undefined}
                            target="_blank"
                            rel="noopener noreferrer"
                            $disabled={!odooOrganizationId}
                        >
                            <TotemImage
                                src="https://odoo.neocorner.fr/web/static/img/favicon.ico"
                                alt="Logo Odoo"
                                size={30}
                            />
                            <span>{odooOrganizationId ? `Client Odoo #${odooOrganizationId}` : "Pas d'ID Odoo"}</span>
                        </ExternalResourceLink>
                    </ExternalResource>
                    <ExternalResource>
                        <ExternalResourceLink
                            href={getAvaLink(avaClientId)}
                            target="_blank"
                            rel="noopener noreferrer"
                            $disabled={!avaClientId}
                        >
                            <TotemImage
                                src="data:image/svg+xml,%3c?xml%20version=%271.0%27%20encoding=%27UTF-8%27?%3e%3csvg%20version=%271.1%27%20id=%27Layer_1%27%20xmlns=%27http://www.w3.org/2000/svg%27%20width=%271063px%27%20height=%27540px%27%20style=%27background-color:%20white;%27%3e%3cg%20transform=%27translate(43,%200)%27%3e%3cpath%20d=%27M4.28450199,387.567034%20L135.551222,118.855464%20C141.812432,100.890629%20158.900832,88%20179,88%20C198.501996,88%20215.169473,100.136014%20221.862223,117.267832%20L352.908923,385.377915%20C356.781572,392.024331%20359,399.753095%20359,408%20C359,432.852814%20338.852814,453%20314,453%20C296.594365,453%20281.496786,443.118042%20274.011634,428.658499%20L273.934,428.698391%20L179.227886,234.511339%20L88.6295131,424.31597%20C81.7891833,441.13904%2065.2797294,453%2046,453%20C20.5949015,453%200,432.405098%200,407%20C0,400.056886%201.53824845,393.47305%204.29253714,387.570699%20Z%27%20id=%27Combined-Shape%27%20fill=%27%23A6BBCC%27%3e%3c/path%3e%3cpath%20d=%27M633.284502,387.567034%20L764.551222,118.855464%20C770.812432,100.890629%20787.900832,88%20808,88%20C827.49645,88%20844.159993,100.129112%20850.856511,117.253217%20L982.589287,386.588189%20C986.04028,392.955444%20988,400.248863%20988,408%20C988,432.852814%20967.852814,453%20943,453%20C923.292835,453%20906.544465,440.331898%20900.453933,422.694739%20L808.227886,234.511339%20L718.025276,423.308595%20C711.443356,440.663606%20694.662337,453%20675,453%20C649.594902,453%20629,432.405098%20629,407%20C629,400.056841%20630.538269,393.472963%20633.292592,387.570582%20Z%27%20id=%27Combined-Shape-Copy%27%20fill=%27%23A0A2A1%27%3e%3c/path%3e%3cpath%20d=%27M237.279652,437.68651%20L427.402701,46.6998626%20C436.341,19.5773943%20461.884227,0%20492,0%20C518.633855,0%20541.691376,15.3121062%20552.849681,37.6134367%20L552.928079,37.6171005%20L749.469527,439.177405%20C755.5191,449.197112%20759,460.942052%20759,473.5%20C759,510.226936%20729.226936,540%20692.5,540%20C664.366733,540%20640.313818,522.529937%20630.597932,497.846488%20L493.102913,216.970504%20L359.555225,496.232857%20C349.792483,521.822569%20325.019025,540%20296,540%20C258.444637,540%20228,509.555363%20228,472%20C228,459.484617%20231.381078,447.758927%20237.27965,437.686513%20Z%27%20id=%27Combined-Shape-Copy-2%27%20fill=%27%23004677%27%20transform=%27translate(493.500000,%20270.000000)%20scale(1,%20-1)%20translate(-493.500000,%20-270.000000)%20%27%3e%3c/path%3e%3c/g%3e%3c/svg%3e"
                                alt="Logo Ava"
                                size={30}
                            />
                        </ExternalResourceLink>
                        <DetailFormValue
                            type="number"
                            step="1"
                            defaultValue={avaClientId}
                            width="100%"
                            {...register('avaClientId', { required: false, valueAsNumber: true })}
                        />
                    </ExternalResource>
                </SectionColumn>
                <SectionColumn>
                    <AdditionalInfoSection organization={organization} />
                    <StatesSection organization={organization} />
                </SectionColumn>
            </ColumnsSectionContainer>
        </SectionContainer>
    );
};

const Header = styled.div`
    display: flex;
`;

const SideValues = styled.div`
    display: flex;
    flex-direction: column;
    margin-left: 15px;

    & > :not(:first-child) {
        margin-top: 15px;
    }
`;

const ExternalResource = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    flex: 1;

    & > :not(:first-child) {
        margin-left: 10px;
    }
`;

const ExternalResourceLink = styled.a<{ $disabled: boolean }>`
    display: flex;
    flex-direction: row;
    align-items: center;

    color: ${({ theme }) => theme.ctaPrimaryColor};
    text-decoration: none;
    opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
    pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'default')};
    cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};

    &:hover {
        text-decoration: underline;
    }

    & > :not(:first-child) {
        margin-left: 10px;
    }
`;
